import mixpanel from "mixpanel-browser";
import { getMixpanelProjectToken, isDev } from "./config";
import { log } from "./log";
import { trackTelemetryInPostgres } from "./telemetryApi";

export class EventLogger {
  private static instance: EventLogger;

  private userId?: string;

  private initialized: Promise<void>;

  private constructor() {
    this.initialized = this.initMixpanelClient();
  }

  public static getInstance() {
    if (!EventLogger.instance) {
      EventLogger.instance = new EventLogger();
    }
    return EventLogger.instance;
  }

  initMixpanelClient = async () => {
    if (!getMixpanelProjectToken()) {
      console.error("mixpanel project token not set");
      trackTelemetryInPostgres({
        name: "mixpanel_init_failed",
        extra: { error: "project token not set" },
      });
      return Promise.reject();
    }

    const TIMEOUT_MS = 10000; // 10 seconds
    let timeoutId: NodeJS.Timeout | null = null;
    let isResolved = false;

    return new Promise<void>((resolve) => {
      // Set up timeout
      timeoutId = setTimeout(() => {
        if (!isResolved) {
          isResolved = true;
          log.debug("[mixpanel] init timeout - did not load within 10 seconds");
          trackTelemetryInPostgres({
            name: "mixpanel_init_timeout",
            extra: { timeoutMs: TIMEOUT_MS },
          });
          // Resolve anyway to not block the app
          resolve();
        }
      }, TIMEOUT_MS);

      try {
        mixpanel.init(getMixpanelProjectToken() || "", {
          debug: isDev(),
          track_pageview: !isDev(),
          persistence: "localStorage",
          loaded: () => {
            if (!isResolved) {
              isResolved = true;
              if (timeoutId) clearTimeout(timeoutId);

              // Check if browser is online
              if (!navigator.onLine) {
                log.debug("[mixpanel] connect failure - browser is offline");
                trackTelemetryInPostgres({ name: "mixpanel_connect_failure" });
              } else {
                log.debug("[mixpanel] init success");
                trackTelemetryInPostgres({ name: "mixpanel_init_success" });
              }
              resolve();
            }
          },
        });
      } catch (error: any) {
        if (!isResolved) {
          isResolved = true;
          if (timeoutId) clearTimeout(timeoutId);
          log.debug("[mixpanel] init failed", error);
          trackTelemetryInPostgres({
            name: "mixpanel_init_failed",
            extra: { error: error?.message || String(error) },
          });
          // Resolve anyway to not block the app
          resolve();
        }
      }
    });
  };

  setUser = (userId: string): void => {
    if (this.userId !== userId) {
      this.initialized.then(() => {
        log.debug(`[mixpanel] setting user ${userId}`);
        mixpanel.identify(userId);
        this.userId = userId;
      });
    }
  };

  track = (originalEvent: ClientEvent) => {
    const event: any = {
      environment: isDev() ? "dev" : "prod",
      ...originalEvent,
    };
    delete event.name;

    if (this.userId) {
      event.distinct_id = this.userId;
    }

    log.debug(`[mixpanel] log event ${JSON.stringify(originalEvent)}`);

    // Mixpanel
    this.initialized.then(() => {
      mixpanel.track(originalEvent.name, event);
    });

    // New Relic
    if (
      typeof window !== "undefined" &&
      (window as any).newrelic?.addPageAction
    ) {
      (window as any).newrelic.addPageAction(originalEvent.name, event);
    }

    // PostgreSQL telemetry
    trackTelemetryInPostgres({
      name: originalEvent.name,
      extra: event,
    });
  };

  timeEvent = (eventName: string) => {
    mixpanel.time_event(eventName);

    if (
      typeof window !== "undefined" &&
      (window as any).newrelic?.addPageAction
    ) {
      (window as any).newrelic.addPageAction(`time_event:${eventName}`, {
        environment: isDev() ? "dev" : "prod",
      });
    }
  };
}

interface Event {
  name: string;
  extra?: any;
}

interface ClickEvent extends Event {
  name: "click";
  action:
    | "open_shipment"
    | "open_mail"
    | "download"
    | "other"
    | "download_attachment";
  rowId?: string;
  attachmentName?: string;
  shipmentId?: string;
  messageId?: string;
  threadId?: string;
}

interface PostFeedbackEvent extends Event {
  name: "post_feedback";
  category: "accessorial_charges" | "generic";
}

interface SearchEvent extends Event {
  name: "search";
  action: "ajww_search_rate" | "ajww_search_quote" | "shipment_search";
  query: string;
}

interface ChatConversationStartedEvent extends Event {
  name: "chat_conversation_started";
  conversationId: string;
  conversationType: string;
  feedId?: string;
  hasInitialMessage: boolean;
}

interface ChatMessageSentEvent extends Event {
  name: "chat_message_sent";
  conversationId: string;
  message: string;
  conversationType: string;
  feedId?: string;
}

interface ChatResponseReceivedEvent extends Event {
  name: "chat_response_received";
  conversationId: string;
  conversationType: string;
  feedId?: string;
  hasResponse: boolean;
  sourcesCount: number;
}

type ClientEvent =
  | ClickEvent
  | SearchEvent
  | PostFeedbackEvent
  | ChatConversationStartedEvent
  | ChatMessageSentEvent
  | ChatResponseReceivedEvent;

export const eventLogger = EventLogger.getInstance();
